import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mapKeys } from 'lodash-es';
import { updateSearchParams } from '@library';
import type { FilterMultiValue, FilterSingleValue } from '..';
import { tireCommonFilters, tirePairFilters } from '..';
import usePagination from '../../../hooks/usePagination';

export interface FilterValues {
    all: FilterMultiValue;
    front: FilterSingleValue;
    back: FilterSingleValue;
}

const useTireParams = () => {
    const [searchParams] = useSearchParams();

    const { navigateToPage } = usePagination();

    const getSideValues = (
        urlSearchParams: URLSearchParams,
        filters: { field: string; urlKey: string }[]
    ) => {
        return Object.fromEntries(
            filters.map(filter => {
                return [
                    filter.field,
                    urlSearchParams.get(filter.urlKey) ?? undefined,
                ];
            })
        );
    };

    const getFilterValues = (
        urlSearchParams: URLSearchParams,
        filters: { field: string }[]
    ) => {
        return Object.fromEntries(
            filters.map(filter => {
                return [filter.field, urlSearchParams.getAll(filter.field)];
            })
        );
    };

    const params = useMemo(() => {
        const all = getFilterValues(searchParams, [
            ...tireCommonFilters,
            ...tirePairFilters,
        ]);
        const front = getSideValues(
            searchParams,
            tirePairFilters.map(filter => {
                return { ...filter, urlKey: `front_${filter.field}` };
            })
        );
        const back = getSideValues(
            searchParams,
            tirePairFilters.map(filter => {
                return { ...filter, urlKey: `back_${filter.field}` };
            })
        );

        return {
            all,
            front,
            back,
        } as FilterValues;
    }, [searchParams]);

    const updateParams = (newParams: Partial<typeof params>) => {
        const { all, front, back, ...plain } = newParams;
        const newFlatParams = {
            ...newParams.all,
            ...mapKeys(newParams.front, (_, key) => `front_${key}`),
            ...mapKeys(newParams.back, (_, key) => `back_${key}`),
            ...plain,
        };
        navigateToPage(
            1,
            updateSearchParams(searchParams, newFlatParams).toString()
        );
    };

    return {
        params,
        updateParams: useCallback(updateParams, [searchParams, navigateToPage]),
    };
};

export default useTireParams;
